import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Layers } from "lucide-react";
import { CHALLENGE_CATEGORIES, MOCK_CHALLENGES } from "@/data/marketplaceMockData";
import { ChallengeCard } from "./marketplace/ChallengeCard";
import { CreateChallengeCard } from "./marketplace/CreateChallengeCard";
import api from "@/lib/api";

// The 5 REAL, live-backend 30-Day tracks — merged into the marketplace grid alongside
// the illustrative community challenges, using REAL enrollment counts from `trackStats`
// (GET /api/public/track-stats, already fetched once on LandingPage.js).
const REAL_TRACKS = [
  { id: "data_cloud", title: "30-Day Data Cloud Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Data Streams", "Identity Resolution", "Segmentation", "Activation"], bossBattle: true, rating: 4.9 },
  { id: "sfmc", title: "30-Day SFMC Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Journey Builder", "Automation Studio", "AMPscript"], bossBattle: true, rating: 4.8 },
  { id: "mc_next", title: "30-Day MC Next Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["DMOs", "Identity Resolution", "Agentforce"], bossBattle: true, rating: 4.8 },
  { id: "admin_dev", title: "30-Day Admin + Dev Challenge", category: "Salesforce", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["Security Model", "Apex", "Automation"], bossBattle: true, rating: 4.9 },
  { id: "servicenow", title: "30-Day ServiceNow Challenge", category: "ServiceNow", creatorName: "Cloudivo", creatorVerified: true, difficulty: "All Levels", durationDays: 30, xp: 6000, skills: ["CMDB", "Flow Designer", "ITSM"], bossBattle: true, rating: 4.8 },
];

const MOCK_IDS = new Set(MOCK_CHALLENGES.map((c) => c.id));

export const ChallengeMarketplaceSection = ({ trackStats, excludeRealTracks = false }) => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [communityChallenges, setCommunityChallenges] = useState([]);

  // Any NEWLY created + published custom AI challenge (by any user, via the AI Challenge
  // Builder below) automatically shows up here too — same card, same /marketplace/challenge/:id
  // preview flow — with zero manual work per new challenge. Challenges already seeded into
  // MOCK_IDS (the 14 curated ones) are skipped here since they're already rendered above
  // with richer cosmetic metadata (skills/rating/etc).
  useEffect(() => {
    api.get("/custom-challenges/marketplace").then((r) => {
      const fresh = (r.data || [])
        .filter((c) => !MOCK_IDS.has(c.id))
        .map((c) => ({
          id: c.id, title: c.title, category: c.category, creatorName: c.creator_name, creatorVerified: false,
          learners: c.learners_count, difficulty: "All Levels", durationDays: c.total_days, xp: 1450,
          skills: [], bossBattle: false, rating: 5.0, isReal: true, isCustomReal: true,
        }));
      setCommunityChallenges(fresh);
    }).catch(() => {});
  }, []);

  const allChallenges = useMemo(() => {
    const real = excludeRealTracks ? [] : REAL_TRACKS.map((t) => ({ ...t, isReal: true, learners: (trackStats && trackStats[t.id]) || 0 }));
    const mock = MOCK_CHALLENGES.map((c) => ({ ...c, isReal: false }));
    return [...real, ...mock, ...communityChallenges];
  }, [trackStats, excludeRealTracks, communityChallenges]);

  const filtered = activeCategory === "All"
    ? allChallenges
    : activeCategory === "Trending"
      ? [...allChallenges].sort((a, b) => b.learners - a.learners).slice(0, 8)
      : allChallenges.filter((c) => c.category === activeCategory);

  return (
    <section id="challenges" className="max-w-7xl mx-auto px-6 py-24" data-testid="challenge-marketplace-section">
      <div className="max-w-2xl mb-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF] flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" /> Challenge Marketplace
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">{excludeRealTracks ? "Beyond Salesforce — every skill has a 30-day path." : "Every skill has a 30-day path."}</h2>
        <p className="text-slate-400 mt-3">Join an AI-powered challenge built by our team or the community — or generate your own in minutes.</p>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-8 -mx-1 px-1" data-testid="challenge-category-pills">
        {CHALLENGE_CATEGORIES.map((cat) => (
          <button
            key={cat}
            data-testid={`category-pill-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActiveCategory(cat)}
            className={`shrink-0 text-xs font-semibold px-4 py-2 rounded-full border transition-colors whitespace-nowrap ${
              activeCategory === cat
                ? "bg-[#5C9DFF] border-[#5C9DFF] text-white"
                : "bg-white/[0.03] border-white/10 text-slate-300 hover:border-white/25 hover:text-white"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="challenge-card-grid">
        {filtered.map((c, i) => <ChallengeCard key={c.id} challenge={c} index={i} />)}
        <CreateChallengeCard />
      </motion.div>

      {filtered.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-14" data-testid="challenge-marketplace-empty-state">No challenges in this category yet — be the first to create one.</p>
      )}
    </section>
  );
};
